import { useCDNStats, useCDNVersion } from '../hooks/useCDN';
import { useExperimentStats } from '../hooks/useExperiments';
import { useContentStats } from '../hooks/useContent';
import { FlaskConical, FileText, Cloud, TrendingUp } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function Dashboard() {
  const { data: cdnStats, isLoading: cdnLoading } = useCDNStats();
  const { data: versionInfo } = useCDNVersion();
  const { data: experimentStats, isLoading: expLoading } = useExperimentStats();
  const { data: contentStats, isLoading: contentLoading } = useContentStats();

  const stats = [
    {
      name: 'Active Experiments',
      value: experimentStats?.active || 0,
      icon: FlaskConical,
      color: 'bg-primary',
      loading: expLoading,
    },
    {
      name: 'Content Items',
      value: contentStats?.total || 0,
      icon: FileText,
      color: 'bg-purple-500',
      loading: contentLoading,
    },
    {
      name: 'CDN Files',
      value: cdnStats?.totalFiles || 0,
      icon: Cloud,
      color: 'bg-green-500',
      loading: cdnLoading,
    },
    {
      name: 'Published',
      value: contentStats?.published || 0,
      icon: TrendingUp,
      color: 'bg-orange-500',
      loading: contentLoading,
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-gray-text">Monitor experiments, content, and CDN status</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="glass-card p-6 animate-spring-in"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.color} p-3 rounded-xl`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <p className="text-gray-text text-sm font-medium mb-1">{stat.name}</p>
              {stat.loading ? (
                <div className="h-8 w-16 bg-dark-lighter rounded animate-pulse" />
              ) : (
                <p className="text-3xl font-bold text-white">{stat.value}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Version Info */}
      {versionInfo && (
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">CDN Content Version</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-gray-text text-sm mb-1">Version</p>
              <p className="text-white font-mono">{versionInfo.version?.substring(0, 12) || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-text text-sm mb-1">Published</p>
              <p className="text-white">
                {versionInfo.timestamp
                  ? formatDistanceToNow(new Date(versionInfo.timestamp), { addSuffix: true })
                  : 'Unknown'}
              </p>
            </div>
            <div>
              <p className="text-gray-text text-sm mb-1">Items</p>
              <p className="text-white">{versionInfo.itemCount || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <a href="/experiments" className="btn-secondary text-center">
            New Experiment
          </a>
          <a href="/dynamic-content" className="btn-secondary text-center">
            Manage Content
          </a>
          <a href="/cdn" className="btn-secondary text-center">
            CDN Files
          </a>
          <a href="/mcp" className="btn-secondary text-center">
            MCP Config
          </a>
        </div>
      </div>
    </div>
  );
}
